import React from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../contexts/ChatContext';
import Avatar from './Avatar';
import ConversationListSkeleton from './ConversationListSkeleton';
import { formatDistanceToNow } from 'date-fns';
import { vi, enUS } from 'date-fns/locale';
import { IoCloudOfflineOutline, IoRefreshOutline } from 'react-icons/io5';
import './ConversationList.css';

const ConversationList = ({ conversations, activeId, onSelect, loading, error, onRetry }) => {
  const { t, i18n } = useTranslation();
  const { onlineUsers } = useChat();
  const dateLocale = i18n.language === 'vi' ? vi : enUS;

  if (loading) {
    return <ConversationListSkeleton count={8} />;
  }

  if (error) {
    return (
      <div className="conversation-list-error">
        <IoCloudOfflineOutline className="error-icon" />
        <p className="error-title">{t('chat.connectionError')}</p>
        <p className="error-message">{t('chat.connectionErrorMessage')}</p>
        <button className="retry-button" onClick={onRetry}>
          <IoRefreshOutline />
          <span>{t('chat.retry')}</span>
        </button>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="conversation-list-empty">
        <p>{t('chat.noConversations')}</p>
      </div>
    );
  }

  return (
    <div className="conversation-list">
      {conversations.map((conv) => {
        const isActive = conv.id === activeId;
        const displayName = conv.is_group
          ? conv.name
          : conv.other_user?.display_name || 'Unknown';
        const avatarUser = conv.is_group ? null : conv.other_user;
        const isOnline = !conv.is_group && conv.other_user && onlineUsers.has(conv.other_user.id);
        const lastMessage = conv.last_message;
        const hasUnread = (conv.unread_count || 0) > 0;

        return (
          <div
            key={conv.id}
            className={`conversation-item ${isActive ? 'active' : ''} ${hasUnread ? 'unread' : ''}`}
            onClick={() => onSelect(conv)}
          >
            <div className="conversation-avatar-wrapper">
              <Avatar user={avatarUser} size="medium" />
              {isOnline && <span className="online-dot" />}
            </div>

            <div className="conversation-info">
              <div className="conversation-top">
                <span className="conversation-name">{displayName}</span>
                {lastMessage && (
                  <span className="conversation-time">
                    {formatDistanceToNow(new Date(lastMessage.created_at), {
                      addSuffix: false,
                      locale: dateLocale,
                    })}
                  </span>
                )}
              </div>

              <div className="conversation-bottom">
                <span className={`conversation-preview ${lastMessage?.is_deleted ? 'deleted' : ''}`}>
                  {lastMessage
                    ? lastMessage.is_deleted
                      ? t('chat.messageDeleted')
                      : (() => {
                          // Check for voice message
                          if (lastMessage.voice_url) {
                            return t('chat.voiceMessage');
                          }
                          // Check for media (image/video)
                          const mediaTypes = lastMessage.media_types || [];
                          if (mediaTypes.includes('image')) {
                            return t('chat.imageMessage');
                          }
                          if (mediaTypes.includes('video')) {
                            return t('chat.videoMessage');
                          }
                          // Default to content
                          return (lastMessage.content || '').substring(0, 60);
                        })()
                    : t('chat.noMessages')}
                </span>
                {hasUnread && (
                  <span className="unread-badge">{conv.unread_count}</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ConversationList;
